import React from "react";
import api from "../api/client";
import { useAuth } from "./AuthContext";
import { useOrg } from "./OrgContext";

export const TERMINOLOGY_DEFAULTS = {
  vendor: "Vendor",
  vendor_plural: "Vendors",
  material: "Material",
  material_plural: "Materials",
  lot: "Lot Number",
  lot_plural: "Lot Numbers",
  type: "Type",
  type_plural: "Types",
};

const BASE_KEYS = ["vendor", "material", "lot", "type"];
const TerminologyContext = React.createContext(null);

function normalizeLabels(labels = {}) {
  const next = { ...TERMINOLOGY_DEFAULTS };
  Object.keys(TERMINOLOGY_DEFAULTS).forEach((key) => {
    const value = labels?.[key];
    if (typeof value === "string" && value.trim()) next[key] = value.trim();
  });
  return next;
}

export function TerminologyProvider({ children }) {
  const { user } = useAuth();
  const { current: currentOrg } = useOrg();
  const cacheRef = React.useRef(new Map());
  const [labels, setLabels] = React.useState(TERMINOLOGY_DEFAULTS);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");

  const reloadTerminology = React.useCallback(async () => {
    const orgKey = currentOrg || "default";
    setLabels(TERMINOLOGY_DEFAULTS);
    setError("");
    if (!user) {
      setLoading(false);
      return { ...TERMINOLOGY_DEFAULTS };
    }
    if (cacheRef.current.has(orgKey)) {
      setLabels(cacheRef.current.get(orgKey));
    }
    setLoading(true);
    try {
      const response = await api.get("/org/terminology");
      const merged = normalizeLabels(response.data?.labels || {});
      cacheRef.current.set(orgKey, merged);
      setLabels(merged);
      setError("");
      return merged;
    } catch (err) {
      setLabels(TERMINOLOGY_DEFAULTS);
      setError(
        err?.response?.data?.error || "Unable to load terminology settings.",
      );
      return { ...TERMINOLOGY_DEFAULTS };
    } finally {
      setLoading(false);
    }
  }, [currentOrg, user]);

  React.useEffect(() => {
    let alive = true;
    const orgKey = currentOrg || "default";
    setLabels(TERMINOLOGY_DEFAULTS);
    setError("");
    if (!user) {
      setLoading(false);
      return () => {
        alive = false;
      };
    }
    if (cacheRef.current.has(orgKey)) {
      setLabels(cacheRef.current.get(orgKey));
    }
    setLoading(true);
    api
      .get("/org/terminology")
      .then((response) => {
        if (!alive) return;
        const merged = normalizeLabels(response.data?.labels || {});
        cacheRef.current.set(orgKey, merged);
        setLabels(merged);
        setError("");
      })
      .catch((err) => {
        if (!alive) return;
        setLabels(TERMINOLOGY_DEFAULTS);
        setError(
          err?.response?.data?.error || "Unable to load terminology settings.",
        );
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [currentOrg, user]);

  const saveTerminology = React.useCallback(
    async (values = {}) => {
      const payload = {};
      Object.keys(TERMINOLOGY_DEFAULTS).forEach((key) => {
        if (Object.prototype.hasOwnProperty.call(values, key))
          payload[key] = values[key] ?? "";
      });
      const response = await api.put("/org/terminology", payload);
      const merged = normalizeLabels(response.data?.labels || {});
      const orgKey = currentOrg || "default";
      cacheRef.current.set(orgKey, merged);
      setLabels(merged);
      setError("");
      return merged;
    },
    [currentOrg],
  );

  const t = React.useCallback(
    (key) => {
      if (!key) return "";
      const value = labels?.[key];
      if (typeof value === "string" && value.trim()) return value.trim();
      const fallback = TERMINOLOGY_DEFAULTS[key];
      return fallback || String(key);
    },
    [labels],
  );

  const tp = React.useCallback(
    (key) => {
      if (!key) return "";
      const pluralKey = `${key}_plural`;
      const value = labels?.[pluralKey];
      if (typeof value === "string" && value.trim()) return value.trim();
      const fallback = TERMINOLOGY_DEFAULTS[pluralKey];
      if (fallback) return fallback;
      return `${t(key)}s`;
    },
    [labels, t],
  );

  const value = React.useMemo(
    () => ({
      labels,
      defaults: TERMINOLOGY_DEFAULTS,
      loading,
      error,
      t,
      tp,
      baseKeys: BASE_KEYS,
      reloadTerminology,
      saveTerminology,
    }),
    [labels, loading, error, t, tp, reloadTerminology, saveTerminology],
  );

  return (
    <TerminologyContext.Provider value={value}>
      {children}
    </TerminologyContext.Provider>
  );
}

export function useTerminology() {
  const ctx = React.useContext(TerminologyContext);
  if (ctx) return ctx;
  const t = (key) => TERMINOLOGY_DEFAULTS[key] || String(key || "");
  const tp = (key) => TERMINOLOGY_DEFAULTS[`${key}_plural`] || `${t(key)}s`;
  return {
    labels: TERMINOLOGY_DEFAULTS,
    defaults: TERMINOLOGY_DEFAULTS,
    loading: false,
    error: "",
    t,
    tp,
    baseKeys: BASE_KEYS,
    reloadTerminology: async () => ({ ...TERMINOLOGY_DEFAULTS }),
    saveTerminology: async () => ({ ...TERMINOLOGY_DEFAULTS }),
  };
}

export default TerminologyProvider;
